import { GetProjectListDocument, GetProjectListQuery, GetProjectListQueryVariables } from "@/graphql";
import gaiupApolloClient from "@/graphql/services/ApolloClient";
import { isStoreResetError } from "@/graphql/services/isStoreResetError";
import { retryAfterStoreReset } from "@/graphql/services/retryAfterStoreReset";
import debounce from "@/services/Utils/debounceNew";
import { useAuthStateStore } from "@/stores/Utils/auth/AuthState";
import * as Sentry from "@sentry/vue";
import { ref } from "vue";
import { ProjectListFetchParams } from "../types/Project";
import getProjectQueryWhere from "../utils/getProjectQueryWhere";

export const useProjectList = () => {
  const authState = useAuthStateStore();
  const projects = ref<GetProjectListQuery["project"]>([]);
  const projectsLoading = ref<boolean>(true);

  const fetchImmediate = async ({ filters, hasAccessAllProjects, orderBy, searchString }: ProjectListFetchParams) => {
    projectsLoading.value = true;

    try {
      // Use Apollo client directly since Vue Apollo query becomes stale after navigation
      const result = await retryAfterStoreReset(() =>
        gaiupApolloClient.query<GetProjectListQuery, GetProjectListQueryVariables>({
          query: GetProjectListDocument,
          fetchPolicy: "network-only",
          context: hasAccessAllProjects
            ? {
                headers: {
                  "x-hasura-role": "accessAllProjects",
                },
              }
            : undefined,
          variables: {
            orderBy,
            ...getProjectQueryWhere(filters, searchString, authState.user?.tenantId),
          },
        })
      );

      if (result?.data) {
        projects.value = result.data.project;
      }
    } catch (error) {
      // A tenant switch resets the store mid-request and refetches the list itself.
      if (!isStoreResetError(error)) Sentry.captureException(error);
    }

    projectsLoading.value = false;
  };

  const debouncedFetchImmediate = debounce((params: ProjectListFetchParams) => {
    fetchImmediate(params);
  }, 500);

  const fetchProjectList = (params: ProjectListFetchParams) => {
    if (params.immediately) {
      fetchImmediate(params);
      return;
    }

    debouncedFetchImmediate(params);
  };

  const upsertProject = (project: GetProjectListQuery["project"][0]) => {
    const index = projects.value.findIndex((p) => p.id === project.id);
    if (index === -1) {
      projects.value = [project, ...projects.value];
    } else {
      // Update existing project
      const updatedProjects = [...projects.value];
      updatedProjects[index] = project;
      projects.value = [...updatedProjects];
    }
  };

  return {
    fetchProjectList,
    projects,
    projectsLoading,
    upsertProject,
  };
};
